import { sortSizeKeys } from './sizeSort';

export interface AdditionalMeasurement {
  name: string;
  values: Record<string, string>;
}

interface BuildSizeTableHtmlOptions {
  sizes: string[];
  measurements: Record<string, Record<string, string>>;
  additionalMeasurements?: AdditionalMeasurement[];
  enabledMeasurements: Iterable<string>;
  isAltGiyim: boolean;
  productName?: string;
  productImage: string;
}

const STANDARD_MEASUREMENTS = ['gogus', 'bel', 'basen', 'boy'];

// Mayo/Bikini gibi yüzme ürünlerinde 3. ölçü "Boy" yerine "Basen" olur.
const SWIMWEAR_TYPES = ['Mayo', 'Bikini'];

const UPDATED_SIZE_GUIDES: Record<string, string> = {
  Ceket: 'ceket-v2.png',
  Mont: 'ceket-v2.png',
  Yelek: 'yelek-v2.png',
  Gömlek: 'gomlek-v2.png'
};

export function resolveSizeGuideImage(productName?: string, fallbackImage = ''): string {
  const updatedImage = productName && UPDATED_SIZE_GUIDES[productName];
  if (!updatedImage) return fallbackImage;

  // Saved HTML is also shown on Ticimax, so app-hosted images need absolute URLs.
  const publicPath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return new URL(`${publicPath}/size-guides/${updatedImage}`, `${window.location.origin}/`).href;
}

const measurementLabels: Record<string, string> = {
  gogus: 'Göğüs Ölçüsü',
  bel: 'Bel Ölçüsü',
  basen: 'Basen Ölçüsü',
  boy: 'Boy Ölçüsü'
};

export function collectSizeTableSizes(
  sizes: Record<string, unknown> | string[] | null | undefined,
  measurements: Record<string, Record<string, string>> = {},
  additionalMeasurements: AdditionalMeasurement[] = []
): string[] {
  const baseSizes = Array.isArray(sizes) ? sizes : Object.keys(sizes || {});
  const measurementSizes = Object.values(measurements).flatMap(values => Object.keys(values || {}));
  const additionalSizes = additionalMeasurements.flatMap(measure => Object.keys(measure.values || {}));

  return sortSizeKeys([...new Set([...baseSizes, ...measurementSizes, ...additionalSizes])]);
}

export function getMeasurementKeysWithValues(
  measurements: Record<string, Record<string, string>>,
  keys: string[] = STANDARD_MEASUREMENTS
): string[] {
  return keys.filter(key => {
    const values = measurements[key] || {};
    return Object.values(values).some(value => String(value || '').trim() !== '');
  });
}

export function buildSizeTableHtml({
  sizes,
  measurements,
  additionalMeasurements = [],
  enabledMeasurements,
  isAltGiyim,
  productName,
  productImage
}: BuildSizeTableHtmlOptions): string {
  const orderedSizes = sortSizeKeys(sizes);
  const enabled = new Set(enabledMeasurements);
  const isSwimwear = !isAltGiyim && !!productName && SWIMWEAR_TYPES.includes(productName);
  const standardOrder = isAltGiyim
    ? ['bel', 'basen', 'boy']
    : isSwimwear
      ? ['gogus', 'bel', 'basen']
      : ['gogus', 'bel', 'boy'];

  let tableRows = '';

  standardOrder.forEach(measureKey => {
    if (enabled.has(measureKey)) {
      tableRows += `
        <tr>
            <td>${measurementLabels[measureKey] || measureKey}</td>
            ${orderedSizes.map(size => `<td>${measurements[measureKey]?.[size] || '-'}</td>`).join('')}
        </tr>`;
    }
  });

  Array.from(enabled)
    .filter(key => !STANDARD_MEASUREMENTS.includes(key))
    .forEach(measureKey => {
      tableRows += `
        <tr>
            <td>${measureKey.charAt(0).toUpperCase() + measureKey.slice(1)} Ölçüsü</td>
            ${orderedSizes.map(size => `<td>${measurements[measureKey]?.[size] || '-'}</td>`).join('')}
        </tr>`;
    });

  additionalMeasurements.forEach(measure => {
    if (measure.name) {
      tableRows += `
        <tr>
            <td>${measure.name}</td>
            ${orderedSizes.map(size => `<td>${measure.values[size] || '-'}</td>`).join('')}
        </tr>`;
    }
  });

  const infoFeatures = isAltGiyim ? `
                        <div class="item">
                            <span>1</span>
                            <div class="txt">
                                <div class="T">Bel Çevresi</div>
                                <p>Ellerinizi belinize koyun. Elinizin bulunduğu nokta bel noktanızdır. O noktadan belinizin çevresini ölçünüz.</p>
                            </div>
                        </div>
                        <div class="item">
                            <span>2</span>
                            <div class="txt">
                                <div class="T">Basen Çevresi</div>
                                <p>Kalçanızın en geniş noktasından mezurayı geçirerek basen çevrenizi ölçünüz.</p>
                            </div>
                        </div>
                        <div class="item">
                            <span>3</span>
                            <div class="txt">
                                <div class="T">Boy Ölçüsü</div>
                                <p>Belden aşağı doğru ürünün bittiği noktaya kadar ölçünüz.</p>
                            </div>
                        </div>` : `
                        <div class="item">
                            <span>1</span>
                            <div class="txt">
                                <div class="T">Göğüs Çevresi</div>
                                <p>Mezurayı göğsünüzün üzerinde en geniş noktasına yerleştirerek göğüsünüzün çevresi için ölçüm yapınız.</p>
                            </div>
                        </div>
                        <div class="item">
                            <span>2</span>
                            <div class="txt">
                                <div class="T">Bel Çevresi</div>
                                <p>Ellerinizi belinize koyun. Elinizin bulunduğu nokta bel noktanızdır. O noktadan belinizin çevresini ölçünüz.</p>
                            </div>
                        </div>
                        <div class="item">
                            <span>3</span>
                            <div class="txt">
                                <div class="T">${isSwimwear ? 'Basen Çevresi' : 'Boy Ölçüsü'}</div>
                                <p>${isSwimwear ? 'Mezurayı baseninizin en geniş noktasına yerleştirerek basen çevresi için ölçüm yapınız.' : 'Ölçüye omuz hizasından başlayın. Dik durunuz ve omzunuzdan aşağı vücudunuza paralel olacak şekilde ölçünüz.'}</p>
                            </div>
                        </div>`;

  const descriptionText = isAltGiyim
    ? 'Alt giyim ürünlerinin bel ve basen ölçüleri vücut ölçülerinize göre değişiklik gösterebilir. Bu sadece bir rehberdir.'
    : '';

  return `<div class="BodyTableModal" id="BedenTabloHtml" style="display: none;">
    <div class="container-fluid">
        <div class="BodyTableLeft">
            <div class="BodyHead">
                <div class="T">Ölçü Rehberi</div>
                <div class="sT">Ölçüler
                    <ul>
                        <li>vücut çevresinin tam ölçüleridir ve cm cinsinden verilmiştir. Bu ölçüler</li>
                        <li>kişisel vücut proporsiyonlarınıza göre değişiklik gösterebilir.</li>
                    </ul>
                </div>
            </div>
            <div class="BodyContent">
                <div class="BodyAccordion">
                    <div class="item Active" data-type="Giyim">
                        <div class="accordion-content">
                            <table cellpadding="0" cellspacing="0">
                                <thead>
                                    <tr>
                                        <th>Bedenler</th>
                                        ${orderedSizes.map(size => `<th>${size}</th>`).join('')}
                                    </tr>
                                </thead>
                                <tbody>
                                    ${tableRows}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="BodyTableRight">
            <div class="T">Doğru Şekilde Nasıl Ölçerim?</div>
            <div class="RightInfo Show Giyim">
                ${descriptionText ? `<p>${descriptionText}</p>` : ''}
                <div class="bedenWrap">
                    <div class="bedenImg">
                        <img alt="" src="${resolveSizeGuideImage(productName, productImage)}" />
                    </div>
                    <div class="InfoFeatures">
                        ${infoFeatures}
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>`;
}
